import type { LaneInfo } from '../api/client';

interface LaneCardProps {
  lane: LaneInfo;
  prices: Record<string, number>;
  todayRealizedUsd: number;
}

function humanSchedule(cron: string): string {
  // Common patterns
  if (cron === '* * * * *') return '每分钟';
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return cron;
  const [minute, hour, dom, month, dow] = parts;
  if (minute === '0' && hour === '*' && dom === '*' && month === '*' && dow === '*') return '每小时整点';
  if (minute !== '*' && hour === '*' && dom === '*' && month === '*' && dow === '*') {
    return `每小时第 ${minute} 分`;
  }
  // e.g. "7 * * * *"
  const minuteNum = parseInt(minute, 10);
  if (!isNaN(minuteNum) && hour === '*' && dom === '*' && month === '*' && dow === '*') {
    return `每小时第 ${minuteNum} 分`;
  }
  return cron;
}

function formatDay(day: number | null): string {
  if (day === null || day === undefined) return '—';
  // day is a Unix timestamp in ms or a YYYYMMDD integer
  if (day > 19000000 && day < 21000000) {
    // YYYYMMDD format
    const s = String(day);
    return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
  }
  // Unix ms timestamp
  return new Date(day).toLocaleDateString('zh-CN', { timeZone: 'UTC' });
}

function computeCurrentR(
  direction: 'LONG' | 'SHORT',
  entryPrice: number,
  stopPrice: number,
  currentPrice: number
): number | null {
  const risk = Math.abs(entryPrice - stopPrice);
  if (risk === 0) return null;
  const dir = direction === 'LONG' ? 1 : -1;
  return ((currentPrice - entryPrice) * dir) / risk;
}

export function LaneCard({ lane, prices, todayRealizedUsd }: LaneCardProps) {
  const symbols = lane.symbols ?? Object.keys(lane.perSymbol ?? {});

  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <div className="card-header">
        <span className="card-title">
          trend-d1 &middot; {lane.mode ?? '—'} &middot; {humanSchedule(lane.schedule)}
        </span>
        <span className="label">Lane ID: {lane.id}</span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {symbols.length === 0 && (
          <span className="muted">无配置品种</span>
        )}
        {symbols.map((sym) => {
          const symData = lane.perSymbol?.[sym];
          const pos = symData?.position ?? null;
          const price = prices[sym];

          if (!pos) {
            return (
              <div key={sym} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, minWidth: '4rem' }}>{sym}</span>
                <span className="muted">— 空仓 —</span>
              </div>
            );
          }

          const rVal = price !== undefined
            ? computeCurrentR(pos.direction, pos.entryPrice, pos.stopPrice, price)
            : null;
          const rColor = rVal === null ? undefined : rVal >= 0 ? '#4ade80' : '#f87171';

          return (
            <div key={sym} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600, minWidth: '4rem' }}>{sym}</span>
              <span style={{
                padding: '2px 6px',
                borderRadius: 4,
                fontSize: '0.75rem',
                fontWeight: 600,
                background: pos.direction === 'LONG' ? 'rgba(74,222,128,0.15)' : 'rgba(248,113,113,0.15)',
                color: pos.direction === 'LONG' ? '#4ade80' : '#f87171',
              }}>
                {pos.direction}
              </span>
              <span className="label">{pos.size} @ {pos.entryPrice}</span>
              <span className="label">&middot; 止损 {pos.stopPrice}</span>
              {rVal !== null && (
                <span style={{ color: rColor, fontWeight: 600, fontSize: '0.85rem' }}>
                  &middot; 当前 {rVal >= 0 ? '+' : ''}{rVal.toFixed(2)}R
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: '0.75rem', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '0.5rem' }}>
        <span className="label">
          上次评估 {formatDay(symbols[0] !== undefined ? (lane.perSymbol?.[symbols[0]]?.lastEvaluatedDay ?? null) : null)}
          &nbsp;&middot;&nbsp;
          当日已实现&nbsp;
          <span style={{ color: Number.isFinite(todayRealizedUsd) && todayRealizedUsd >= 0 ? '#4ade80' : '#f87171', fontWeight: 600 }}>
            {Number.isFinite(todayRealizedUsd) && todayRealizedUsd >= 0 ? '+' : ''}{Number.isFinite(todayRealizedUsd) ? todayRealizedUsd.toFixed(2) : '0.00'} USD
          </span>
        </span>
      </div>
    </div>
  );
}
